import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import type { RegistrationById, UpdateRegistrationPayload } from "../../../api/registrations.types";
import { useUpdateRegistration } from "../../../api/registrations";
import * as FormDialog from "../../../shared/FormDialog";
import { useRegistrationVariant } from "../RegistrationVariantProvider";

type RegistrationEditDialogProps = {
  onClose: () => void;
  registration: RegistrationById;
};

export const RegistrationEditDialog: FC<RegistrationEditDialogProps> = ({ onClose, registration }) => {
  const { t } = useTranslation();
  const variant = useRegistrationVariant();
  const { handleSubmit, control, setFocus } = useForm<UpdateRegistrationPayload>({
    defaultValues: { name: registration.name },
  });
  const { mutate: updateRegistration, isError, isPending } = useUpdateRegistration();

  FormDialog.useAutoFocusField(setFocus, "name");

  const handleUpdateRegistration = (payload: UpdateRegistrationPayload) => {
    updateRegistration({ registrationId: registration.id, payload }, { onSuccess: onClose });
  };

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("RegistrationDrawer.Edit.DialogHeader")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(handleUpdateRegistration)}>
        <FormDialog.Content>
          <FormDialog.FormError isError={isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("RegistrationDrawer.Edit.Error") }}
              label={t("RegistrationDrawer.Edit.NameLabel")}
              placeholder={t(`CreateRegistration.Dialog.${variant}.NamePlaceholder`)}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={isPending}>
            {t("RegistrationDrawer.Edit.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
